import {
    EXAMPLE_RESUME,
    shownLine,
    skillsReordered,
    type ExampleLine,
    type ResumeVersionView,
} from "@/lib/example";

/** One line, marked when the tailor changed it and the tailored version is shown. */
function Line({ line, view }: { line: ExampleLine; view: ResumeVersionView }) {
    const shown = shownLine(line, view);
    if (!shown.changed) return <>{shown.text}</>;
    return (
        <mark className="rounded-[4px] bg-tint-green px-0.5 text-fg [box-decoration-break:clone]">
            {shown.text}
        </mark>
    );
}

/**
 * Jordan Lee's resume as a page: Original as Jordan wrote it, Tailored as
 * it reads for the example job, with every changed line marked. Readable
 * at full contrast: the example is the point, not a picture of one.
 */
export default function ExampleResume({ view }: { view: ResumeVersionView }) {
    const resume = EXAMPLE_RESUME;
    const skills = view === "tailored" ? resume.skills.tailored : resume.skills.original;
    const reordered = view === "tailored" && skillsReordered(resume);
    return (
        <article
            aria-label={`Example resume, ${view === "tailored" ? "tailored" : "original"}`}
            className="flex flex-col gap-4 rounded-2xl border border-line bg-card px-6 py-[22px] shadow-card max-[560px]:px-4"
        >
            <header className="flex flex-col gap-1">
                <h2 className="m-0 font-display text-[24px] font-bold tracking-[-0.02em]">
                    {resume.name}
                </h2>
                <p className="m-0 text-[13px] text-fg-muted">
                    <span className="font-semibold text-fg">{resume.headline}</span> ·{" "}
                    {resume.where}
                </p>
            </header>
            <p className="m-0 max-w-[68ch] text-[14px] leading-[1.6]">
                <Line line={resume.summary} view={view} />
            </p>
            <section className="flex flex-col gap-3.5" aria-label="Experience">
                {resume.roles.map((role) => (
                    <div key={role.company} className="flex flex-col gap-1.5">
                        <p className="m-0 flex flex-wrap items-baseline justify-between gap-x-3 text-[14px]">
                            <span>
                                <span className="font-semibold">{role.title}</span> · {role.company}
                            </span>
                            <span className="text-[12.5px] text-fg-faint">{role.span}</span>
                        </p>
                        <ul className="m-0 flex list-disc flex-col gap-1 pl-5 text-[13.5px] leading-[1.55]">
                            {role.lines.map((line) => (
                                <li key={line.original}>
                                    <Line line={line} view={view} />
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </section>
            <section className="flex flex-col gap-2" aria-label="Skills">
                <p className="m-0 flex items-center gap-2 text-[12.5px] font-semibold text-fg-muted">
                    Skills
                    {reordered && (
                        <span className="rounded-full bg-tint-green px-2 py-px text-[11px] font-semibold text-brand-ink">
                            Reordered for this job
                        </span>
                    )}
                </p>
                <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
                    {skills.map((skill) => (
                        <li
                            key={skill}
                            className="rounded-full bg-card-raised px-2.5 py-[3px] text-[12px] font-medium"
                        >
                            {skill}
                        </li>
                    ))}
                </ul>
            </section>
        </article>
    );
}
